import type { ReactNode } from 'react';
import { ACTIVITY, AGENTS, FLEET, MANAGE_ACCESS } from './demo';
import type { ActivityRow, AgentRow, FleetRow, ToolRow } from './demo';

/**
 * Product windows: hand-built, accurate renderings of real Statio screens,
 * drawn in the site's own type so they stay crisp at any size and can be
 * placed on the paintings. Real captures of the same screens live in
 * public/media; use these where the design needs control, captures where it
 * needs proof. Browse them all at /kit.
 *
 * Styles: .pw-* in src/styles/statio.css. Data: ./demo.ts.
 */

export function ProductWindow({
  title,
  children,
  className,
}: {
  title: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={className ? `pw ${className}` : 'pw'}
      role="img"
      aria-label={`Statio app: ${title} (illustrative data)`}
    >
      <div className="pw-bar" aria-hidden="true">
        <i />
        <i />
        <i />
        <span>{title}</span>
      </div>
      <div className="pw-body" aria-hidden="true">
        {children}
      </div>
    </div>
  );
}

const Note = ({ n }: { n?: number }) => (n ? <span className="pw-n">{n}</span> : null);

export function ManageAccessWindow({
  tools = MANAGE_ACCESS.tools,
  annotate = false,
  showHelp = true,
}: {
  tools?: ToolRow[];
  annotate?: boolean;
  showHelp?: boolean;
}) {
  const m = MANAGE_ACCESS;
  return (
    <ProductWindow title="app.statio.dev · Access Control">
      <div className="pw-head">
        <b>Manage Access</b>
        <span className="s-data">
          {m.principal} → {m.server}
          {annotate && <Note n={1} />}
        </span>
      </div>
      <div className="pw-facts">
        <span>
          Can invoke <b>{m.canInvoke}</b>
        </span>
        <span>
          Direct Grant <b>{m.directGrant}</b>
        </span>
        <span>
          Team Access <b>{m.teamAccess}</b>
        </span>
      </div>
      <div className="pw-label">
        <span>Per-tool access</span>
        <span>EXECUTE</span>
      </div>
      {tools.map((t) => (
        <div className="pw-tool" key={t.name}>
          <span>
            {t.name}
            {annotate && <Note n={t.note} />}
          </span>
          {t.state === 'denied' ? (
            <span className="pw-tag pw-no">denied</span>
          ) : t.state === 'via-server' ? (
            <span className="pw-tag pw-ok">via server access</span>
          ) : (
            <span />
          )}
          <span className="pw-btn">{t.state === 'denied' ? 'Allow' : 'Deny'}</span>
        </div>
      ))}
      {showHelp && (
        <p className="pw-help">
          Server-wide access already covers every tool. Turning a tool off here records a deny for this member, which
          overrides the server-wide grant for that tool only.
        </p>
      )}
    </ProductWindow>
  );
}

const AGENT_TAG: Record<AgentRow['status'], string> = {
  active: 'pw-ok',
  suspended: 'pw-hold',
  revoked: 'pw-off',
  'Needs attestation': 'pw-hold',
};

export function AgentsWindow({ rows = AGENTS }: { rows?: AgentRow[] }) {
  return (
    <ProductWindow title="app.statio.dev · Agents">
      <table>
        <thead>
          <tr>
            <th>AGENT</th>
            <th>STATUS</th>
            <th>LAST USED</th>
            <th className="hide-sm">EXPIRES</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((a) => (
            <tr key={a.name}>
              <td className="s-data">{a.name}</td>
              <td>
                <span className={`pw-tag ${AGENT_TAG[a.status]}`}>{a.status}</span>
              </td>
              <td>{a.lastUsed}</td>
              <td className="hide-sm">{a.expires}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </ProductWindow>
  );
}

export function ActivityWindow({ rows = ACTIVITY }: { rows?: ActivityRow[] }) {
  return (
    <ProductWindow title="app.statio.dev · Activity">
      {rows.map((e, i) => (
        <div className="pw-ev" key={i}>
          <div className="r1">
            <span className={`pw-tag ${e.outcome === 'allowed' ? 'pw-ok' : 'pw-no'}`}>{e.outcome}</span>
            <b className="s-data">{e.tool}</b>
            <span>on {e.server}</span>
            <span className="meta">
              {e.ms} ms · {e.code}
            </span>
          </div>
          <div className="r2">{e.caller}</div>
          {e.error && <div className="err">{e.error}</div>}
        </div>
      ))}
    </ProductWindow>
  );
}

export function ConfigWindow() {
  return (
    <ProductWindow title="~/.cursor/mcp.json">
      <div className="pw-cfg">
        <div>
          <span className="pw-label" style={{ marginTop: 0 }}>
            <span>Before</span>
          </span>
          <pre>
            {'{\n  "stripe": '}
            <span className="secret">"sk_live_…"</span>
            {',\n  "github": '}
            <span className="secret">"ghp_…"</span>
            {',\n  "slack":  '}
            <span className="secret">"xoxb-…"</span>
            {'\n}'}
          </pre>
        </div>
        <div>
          <span className="pw-label" style={{ marginTop: 0 }}>
            <span>With Statio</span>
          </span>
          <pre>
            {'{\n  "statio": '}
            <span className="token">"stk_…"</span>
            {'\n}'}
          </pre>
        </div>
      </div>
    </ProductWindow>
  );
}

export function FleetWindow({ rows = FLEET }: { rows?: FleetRow[] }) {
  return (
    <ProductWindow title="app.statio.dev · Fleet">
      <table>
        <thead>
          <tr>
            <th>NAME</th>
            <th className="hide-sm">TYPE</th>
            <th>MEMBER</th>
            <th className="hide-sm">CLIENT</th>
            <th>STATUS</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((f) => (
            <tr key={`${f.name}-${f.member}`}>
              <td className="s-data">{f.name}</td>
              <td className="hide-sm">{f.type}</td>
              <td>{f.member}</td>
              <td className="hide-sm">{f.client}</td>
              <td>
                <span className={`pw-tag ${f.status === 'Managed' ? 'pw-ok' : 'pw-hold'}`}>{f.status}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </ProductWindow>
  );
}

export { ACTIVITY, AGENTS, FLEET, MANAGE_ACCESS };
